if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      // Skip lazy loading if this is inside a quick-add-drawer
      if (this.closest('quick-add-drawer')) {
        return;
      }

      // Always initialize immediately when element is present
      // This ensures inventory display works correctly when user is logged in
      this.initLazySection();
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();

      // Bind to on:variant:change event for this product
      // Try multiple parent selectors to ensure we find the variant picker
      const productInfo = this.closest('.product-info');
      const variantPicker = productInfo ? productInfo.querySelector('variant-picker') : null;

      // Also listen for variant changes on the document level as a fallback
      // This ensures we catch variant changes even if the variant picker isn't found
      document.addEventListener('on:variant:change', this.handleVariantChangeGlobal.bind(this));

      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      } else {
        // If variant picker not found immediately, wait a bit and try again
        // This handles cases where custom elements are initialized asynchronously
        setTimeout(() => {
          const retryPicker = productInfo ? productInfo.querySelector('variant-picker') : null;
          if (retryPicker) {
            retryPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
          }
        }, 100);
      }

      // Additional fallback: Listen for URL changes (for when variant changes update the URL)
      // This is critical for logged-in users where other systems might interfere with events
      if (window.MutationObserver) {
        this.setupURLWatcher();
      }

      // Init - get the current variant from URL or default
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');

      if (variantId && this.variantInventory) {
        // If we have a variant ID in the URL, use that variant's inventory
        const variant = this.variantInventory.find(v => v.id === parseInt(variantId, 10));
        if (variant) {
          this.updateInventory(
            variant.inventory_quantity,
            variant.available,
            variant.inventory_policy
          );
          return;
        }
      }

      // Otherwise use the data attributes
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy
        );
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     * Handles global variant change events (fallback for when direct event doesn't fire)
     * @param {object} evt - Event object
     */
    handleVariantChangeGlobal(evt) {
      // Check if this event is for our product
      const productInfo = this.closest('.product-info');
      if (!productInfo || !evt.detail.variant) return;

      // Only handle if the event came from our product's variant picker
      const eventTarget = evt.target;
      if (eventTarget && productInfo.contains(eventTarget)) {
        this.handleVariantChange(evt);
      }
    }

    /**
     * Sets up a watcher for URL changes to detect variant switches
     * This is a critical fallback for logged-in users where events may be blocked
     */
    setupURLWatcher() {
      let lastVariantId = new URLSearchParams(window.location.search).get('variant');

      // Watch for URL changes
      const checkURLChange = () => {
        const currentVariantId = new URLSearchParams(window.location.search).get('variant');

        if (currentVariantId && currentVariantId !== lastVariantId) {
          lastVariantId = currentVariantId;

          // Find the variant inventory data
          const variant = this.variantInventory.find(v => v.id === parseInt(currentVariantId, 10));
          if (variant) {
            this.updateInventory(
              variant.inventory_quantity,
              variant.available,
              variant.inventory_policy
            );
          }
        }
      };

      // Check every 500ms (lightweight check)
      setInterval(checkURLChange, 500);

      // Also listen for popstate events (browser back/forward)
      window.addEventListener('popstate', checkURLChange);
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     */
    updateInventory(count, available, inventoryPolicy) {
      // Don't skip update even if hidden - we need to update and then show/hide based on conditions
      let inventoryLevel;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not
        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = '';
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
